import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FormDesignerSection, FormSectionType, FormSectionTypeNameById} from 'forms/form-designer/Constants';
import HelpButton from 'help/HelpButton';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Label from 'ui-library/bs/Label';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import ReorderableSimpleTable from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';

import {arrayMove} from './util';

const SECTION_TYPE_HELP_TEXT = (
  <>
    <p>
      <b>Shared sections</b> are only filled out once in the form. Values that are entered in shared sections
      apply to all tasks created in the form.
    </p>
    <p>
      <b>Repeatable sections</b> can be filled out multiple times in the form–once for each task that gets
      created.
    </p>
    <p>There will exist only 1 repeatable section on a form, only shared sections can be added.</p>
  </>
);

export interface FormDesignerSectionsTableProps {
  sections: Array<FormDesignerSection>;
  onAddSection: () => void;
  onRemoveSection: (sectionKey: string) => void;
  onUpdateSection: (sectionKey: string, sectionUpdate: Partial<FormDesignerSection>) => void;
  onUpdateSections: (sections: Array<FormDesignerSection>) => void;
}

export default function FormDesignerSectionsTable({
  sections,
  onAddSection,
  onRemoveSection,
  onUpdateSection,
  onUpdateSections,
}: FormDesignerSectionsTableProps): JSX.Element {
  const getSectionRows = (): Array<Row> =>
    sections.map((section, index) => {
      const {key} = section;
      return {
        id: key,
        cells: [
          {
            key: 'index',
            contents: index + 1,
            cellClassName: css(styles.indexCell),
          },
          {
            key: 'name',
            contents: (
              <TextField
                value={section.name}
                onChange={(e) => {
                  const name = e.target.value;
                  // if the field is empty, then we set isNameVisible to false
                  onUpdateSection(key, {name, isNameVisible: Boolean(name)});
                }}
                onBlur={(e) => {
                  // when we blur the field we want to trim any extra whitespace
                  const name = e.target.value.trim();
                  // if the field is empty, then we set isNameVisible to false
                  onUpdateSection(key, {name, isNameVisible: Boolean(name)});
                }}
                labelStyle={LabelStyle.HIDDEN}
              />
            ),
            cellClassName: css(styles.nameCell),
          },
          {
            key: 'description',
            contents: (
              <TextField
                value={section.description ?? ''}
                onChange={(e) => {
                  const description = e.target.value;
                  // if the description is an empty string, just make it undefined
                  onUpdateSection(key, {description: description || undefined});
                }}
                onBlur={(e) => {
                  // when we blur the field we want to trim any extra whitespace
                  const description = e.target.value.trim();
                  // if the description is an empty string, just make it undefined
                  onUpdateSection(key, {description: description || undefined});
                }}
                labelStyle={LabelStyle.HIDDEN}
              />
            ),
            cellClassName: css(styles.descriptionCell),
          },
          {
            key: 'type',
            contents: FormSectionTypeNameById[section.sectionType],
            cellClassName: css(styles.typeCell),
          },
        ],
        actionGroup: section.sectionType === FormSectionType.STATIC && sections.length > 1 && (
          <Button
            buttonStyle={ButtonStyle.CIRCLE_ICON}
            size={ButtonSize.SMALL}
            icon="delete"
            isDropdown={false}
            iconAltText="Remove section"
            onClick={() => {
              onRemoveSection(key);
            }}
          />
        ),
        rowClassName: css(styles.sectionsTableRow),
      };
    });

  return (
    <div className="u-spacing-bottom">
      <Label>Form sections</Label>
      <ReorderableSimpleTable
        headerCells={[
          {
            key: 'index',
            contents: null,
            cellClassName: css(styles.indexCell),
          },
          {
            key: 'name',
            contents: 'Section name (optional)',
            cellClassName: css(styles.nameCell),
          },
          {
            key: 'description',
            contents: 'Section description (optional)',
            cellClassName: css(styles.descriptionCell),
          },
          {
            key: 'type',
            contents: (
              <span>
                Type
                <HelpButton
                  renderContent={() => SECTION_TYPE_HELP_TEXT}
                  iconClassName="u-mini-spacing-left"
                />
              </span>
            ),
            cellClassName: css(styles.typeCell),
          },
        ]}
        footerCells={[
          {
            key: 'add',
            contents: (
              <Button
                className={css(styles.addButton)}
                buttonStyle={ButtonStyle.TEXT}
                size={ButtonSize.SMALL}
                icon="compose"
                text="Add section"
                onClick={onAddSection}
              />
            ),
            cellClassName: css(styles.addButtonCell),
          },
        ]}
        isDraggingDisabled={sections.length <= 1}
        noRowsDisplay="No sections added"
        rows={getSectionRows()}
        onReorderRow={(sectionKey: string, newIndex: number) => {
          onUpdateSections(
            arrayMove(
              sections,
              sections.findIndex((s) => s.key === sectionKey),
              newIndex
            )
          );
        }}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  sectionsTableRow: {
    padding: '7px 14px',
  },
  addButtonCell: {
    margin: '-4px auto',
  },
  addButton: {
    marginLeft: '-10px',
  },
  indexCell: {
    flex: '0 0 26px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
  },
  nameCell: {
    flexGrow: 2,
  },
  descriptionCell: {
    flexGrow: 3,
  },
  typeCell: {
    flexGrow: 1,
    marginRight: '20px',
    color: GRAY_DARKER,
  },
});
